import Link from 'next/link';
import { ROUTES } from '#/data/constants';
import type { PlanLimits } from '#/types/plan-limits';

interface PlanUsageBadgeProps {
  planLimits: PlanLimits;
}

/**
 * 로그인 사용자의 플랜 이름 · 어절 사용량 · 업그레이드 유도 표시 배지
 */
export default function PlanUsageBadge({ planLimits }: PlanUsageBadgeProps) {
  const { planName, usage, isFreeUser } = planLimits;
  const isUnlimited = usage.totalQuota === null || planLimits.isAdmin;
  const onetimeBalance = usage.onetimeEojeolBalance ?? 0;
  const overageEojeol = usage.overageEojeol ?? 0;

  // 어절 쿼터 소진 시 (단건결제 잔액이 있으면 소진 아님)
  const exhausted =
    !isUnlimited &&
    usage.eojeolRemaining !== null &&
    usage.eojeolRemaining <= 0 &&
    onetimeBalance <= 0;

  if (exhausted) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        <span className="text-red-500 font-semibold">
          이번 달 어절 한도를 모두 사용했습니다.
        </span>
        {isFreeUser && (
          <>
            <Link
              href={ROUTES.PRODUCT ?? '/product'}
              className="text-primary font-semibold underline underline-offset-2 hover:text-primary/80 transition-colors"
            >
              플랜 업그레이드
            </Link>
            <span className="text-muted-foreground">
              하면 더 많이 사용할 수 있어요!
            </span>
          </>
        )}
      </span>
    );
  }

  // 구독 한도 소진 but 단건결제 잔액으로 사용 중인 경우
  const usingOnetime =
    !isUnlimited &&
    usage.eojeolRemaining !== null &&
    usage.eojeolRemaining <= 0 &&
    onetimeBalance > 0;

  // 80% 이상 사용 시 경고 (단건결제 잔액으로 사용 중이면 경고 대신 안내)
  const isWarning = !isUnlimited && !usingOnetime && usage.usagePercent >= 80;

  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
          isFreeUser
            ? 'bg-[#104573]/10 text-muted-foreground dark:bg-[#104573]/10 dark:text-muted-foreground'
            : 'bg-primary/10 text-primary'
        }`}
      >
        {planName}
      </span>
      {usingOnetime && (
        <>
          <span className="text-xs text-amber-600 font-medium">
            남은 어절: {onetimeBalance.toLocaleString()}어절
          </span>
        </>
      )}
      {!isUnlimited && !usingOnetime && (
        <>
          어절:{' '}
          <strong className={isWarning ? 'text-orange-500' : 'text-primary'}>
            {usage.eojeolUsed.toLocaleString()}
          </strong>
          /{usage.totalQuota!.toLocaleString()}
          <span className="text-xs text-amber-600">
            +단기 사용권 {onetimeBalance.toLocaleString()}
          </span>
          {overageEojeol > 0 && (
            <span className="text-xs text-red-500 font-medium">
              (초과 {overageEojeol.toLocaleString()}어절)
            </span>
          )}
          {isWarning && !exhausted && overageEojeol === 0 && (
            <span className="text-xs text-orange-500">
              ({usage.usagePercent}% 사용)
            </span>
          )}
        </>
      )}
      {isUnlimited && <span className="text-xs">어절 무제한</span>}
      {isFreeUser && (
        <span className="text-xs">
          ·{' '}
          <Link
            href={ROUTES.PRODUCT ?? '/product'}
            className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
          >
            업그레이드
          </Link>
        </span>
      )}
    </span>
  );
}
